import { createContext, type ReactNode, useEffect, useMemo, useReducer } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { consumeFreshArrival, hasFreshArrival } from '../stores/freshArrivals';
import { navPathAtom, updateNavPathAtom } from '../stores/navPathAtoms';
import { isHistoryEntryShown } from '../utils/historyEntry';
import { adoptReloadedEntry, currentHistoryKey, entryKey } from '../utils/historyKey';
import { commitNavEntry, type NavPath, resolveNavPath, toNavLocation } from '../utils/navPath';
export type NavigationPathValue = {
  /** 맨 오른쪽 칸이 지금 화면이다. */
  path: NavPath;
  labels: Record<string, string>;
};

/** Provider 밖에서 읽으면 `useNavigationPath` 가 던진다. */
export const NavigationPathContext = createContext<NavigationPathValue | null>(null);

/** 지금 화면까지의 길을 렌더마다 한 번 계산해 내려주고, 화면을 옮기면 같은 entry 로 기록한다. */
export function NavigationPathProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    pathname,
    asPath,
    locales
  } = useRouter();
  const state = useAtomValue(navPathAtom);
  const updateNavPath = useSetAtom(updateNavPathAtom);
  // Next 는 문서의 첫 popstate 가 같은 주소면 라우터를 갱신하지 않는다 — key 만 바뀌어도 다시 그린다.
  const [, rerenderOnPopState] = useReducer((count: number) => count + 1, 0);

  // 기록 전 첫 렌더에도 맞도록 예약을 본다. 소비는 기록할 때 한다.
  const startsNewPath = hasFreshArrival(toNavLocation(asPath));
  // 주소가 같아도 history 항목이 바뀌면(뒤로·앞으로 가기로 같은 URL 의 다른 항목) 그 항목을 기록해야 한다.
  // 다른 주소로 가는 중이면 라우터가 따라올 때까지 지금 화면의 항목(마지막 기록)을 그대로 쓴다.
  const historyKey = isHistoryEntryShown(asPath, locales) || state.currentKey === null ? currentHistoryKey() : state.currentKey;
  const key = entryKey(state, asPath, historyKey);
  const value = useMemo(() => ({
    path: resolveNavPath(state, {
      key,
      pathname,
      asPath,
      startsNewPath
    }),
    labels: state.labels
  }), [state, key, pathname, asPath, startsNewPath]);
  useEffect(() => {
    window.addEventListener('popstate', rerenderOnPopState);
    return () => window.removeEventListener('popstate', rerenderOnPopState);
  }, []);
  useEffect(() => {
    const startsNewPath = consumeFreshArrival(toNavLocation(asPath));
    updateNavPath(prev => commitNavEntry(adoptReloadedEntry(prev, asPath), {
      key: historyKey,
      pathname,
      asPath,
      startsNewPath
    }));
  }, [updateNavPath, pathname, asPath, historyKey]);
  return <NavigationPathContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="NavigationPathProvider" data-sentry-source-file="NavigationPathProvider.tsx">
      {children}
    </NavigationPathContext.Provider>;
}